import type { AddressInfo } from 'src/helper/shared-types';
import { formatNumber } from 'src/helper/utils';
import type {
  CreateQuoteFormValues,
  QuoteApproveFormField,
  QuoteDetailsObj,
  QuoteOption,
  QuoteTask,
} from 'src/redux/work-order/types';

export type QuoteResponse = {
  quote_id: string;
  quote_no: number;
  abilities?: QuoteDetailsObj['abilities'];
  work_order_id?: string;
  status: {
    label: string;
    value: string;
    color_class: string | null;
  };
  valid_until: string;
  time_zone?: string;
  approved_by?: string;
  approved_at?: string;
  approved_note?: string;
  rejected_by?: string;
  rejected_at?: string;
  rejected_note?: string;
  submitted_by: string;
  submitted_at: string;
  rejected_reason?: string;
  rejected_reason_note?: string;
  submitted_user_type:
    | 'account_user'
    | 'technician'
    | 'resident'
    | 'asset_owner'
    | 'lula_pro_network'
    | 'vendor';
  reason?: string;
  tasks: QuoteTaskResponse[];
  address?: AddressInfo;
  pdf_link?: string;
  work_orders?: {
    abilities: Array<string>;
    work_order_id: string;
    status: {
      label: string;
      value: string;
    };
  };
  quote_estimation_range: {
    min_in_cents: number;
    max_in_cents: number;
  };
  trip_fee_in_cents: number;
  default_trip_fee_in_cents: number;
  trip_fee_type: 'pay_trip_fee' | 'pay_if_rejected' | 'only_pay_if_rejected' | 'never_pay';
  customer_approval?: QuoteDetailsObj['customer_approval'];
};

export type ReviewedQuoteTask = {
  quote_task_id: string;
  selected_option: string;
  task_type: 'standard' | 'optional';
};

export type ReviewedQuoteTerms = {
  tasks: ReviewedQuoteTask[];
  trip_fee_type?: 'pay_trip_fee' | 'only_pay_if_rejected' | 'never_pay';
  trip_fee_in_cents?: number;
};

export const canSendQuoteForCustomerApproval = (abilities: QuoteDetailsObj['abilities']): boolean =>
  abilities?.includes('send_to_customer') ?? false;

export const normalizeTripFeeTypeForForm = (
  tripFeeType: QuoteDetailsObj['trip_fee_type'],
): QuoteApproveFormField['trip_fee_type'] =>
  tripFeeType === 'only_pay_if_rejected' ? 'pay_if_rejected' : tripFeeType;

export const buildReviewedQuoteTerms = (formData: QuoteApproveFormField): ReviewedQuoteTerms => {
  const tasks: ReviewedQuoteTask[] = Object.entries(formData.selected_options || {}).map(
    ([quote_task_id, selected_option]) => ({
      quote_task_id,
      selected_option,
      task_type: 'standard',
    }),
  );

  const optionalTasks: ReviewedQuoteTask[] = (formData.optional_selected || []).map(
    (quote_task_id) => ({
      quote_task_id,
      selected_option: formData.optional_selected_data[quote_task_id],
      task_type: 'optional',
    }),
  );

  const tripFeeType =
    formData.trip_fee_type === 'pay_if_rejected' ? 'only_pay_if_rejected' : formData.trip_fee_type;
  let tripFeeInCents: number | undefined;

  if (formData.trip_fee_type === 'pay_if_rejected') {
    tripFeeInCents = Math.round(Number(formData.trip_fee_amount_rejected) * 100);
  } else if (formData.trip_fee_type === 'pay_trip_fee') {
    tripFeeInCents = Math.round(Number(formData.trip_fee_amount_always) * 100);
  }

  return {
    tasks: [...tasks, ...optionalTasks],
    trip_fee_type: tripFeeType,
    trip_fee_in_cents: tripFeeInCents,
  };
};

type QuoteTaskResponse = {
  quote_task_id: string;
  work_order_issue_id?: string;
  work_order_issue: {
    issue_id: string;
    status: {
      value: string;
      label: string;
    };
    abilities: string[];
  };
  title: string;
  selected_option?: string;
  task_type: 'standard' | 'optional';
  options: QuoteOptionResponse[];
};

type QuoteOptionResponse = {
  option_id: string;
  option_type: 'replace' | 'repair';
  description: string;
  labor_cost_in_cents: string;
  markup_fee_in_cents: string;
  markup_fee_type: 'fixed' | 'percentage';
  markup_fee_type_value: string;
  media: Media[];
  materials: Material[];
};

type Media = {
  media_uuid: string;
  media_type: string | null;
  mime_type: string;
  thumbnail: string;
  original: string;
};

type Material = {
  material_id: string;
  label: string;
  markup_fee_in_cents: number;
  markup_fee_type_value: string;
  markup_fee_type: 'fixed' | 'percentage';
  material_cost_in_cents?: number;
  cost_in_cents: number;
  quantity: number;
  quantity_type: string;
  cost_type: 'per-unit' | 'total';
};

/**
 * Calculates the total estimate for a given quote option, including labor and markup.
 */
export const getTotalEstimateOfEachOption = (opt: QuoteOption) => {
  const labor = Number(opt?.labor_cost_in_cents) / 100 || 0;

  const markUp = Number(opt?.markup_fee_type_value) || 0;

  const markUpValue = opt?.markup_fee_type === 'percentage' ? (labor * markUp) / 100 : markUp;

  // TODO : Need to add Markup values in calculations
  const materialTotal = opt?.materials?.reduce(
    (sum, item) => sum + (Number(item?.total_cost_in_cents_including_markup) ?? 0) / 100,
    0,
  );

  return Number(labor) + Number(markUpValue) + Number(materialTotal);
};

/**
 * Calculates the total or range (min/max) for a given quote task, depending on
 * whether an option is selected.
 */
export const getTasksTotalOrRange = (task: QuoteTask, forEstimation: boolean = false) => {
  if (!Array.isArray(task?.options) || task?.options?.length === 0) return { min: 0, max: 0 };

  if (task?.selected_option) {
    const taskOption = task?.options?.find((option) => option?.option_id === task?.selected_option);
    const total = taskOption ? getTotalEstimateOfEachOption(taskOption) : 0;
    return { min: total, max: total };
  }
  if ((!forEstimation && task?.task_type === 'standard') || forEstimation) {
    const totals = task?.options?.map(getTotalEstimateOfEachOption);
    const min = Math.min(...totals);
    const max = Math.max(...totals);
    return { min, max };
  }
  return { min: 0, max: 0 };
};

/**
 * Aggregates the total min and max values for an array of quote tasks.
 */
export const getQuoteTotalRange = (tasks: QuoteTask[], canIncludeOptionalTask: boolean) =>
  tasks?.reduce(
    (acc, task) => {
      if (!canIncludeOptionalTask && task?.task_type === 'optional') {
        return acc;
      }
      const { min, max } = getTasksTotalOrRange(task, false);
      acc.min += min;
      acc.max += max;
      return acc;
    },
    { min: 0, max: 0 },
  );

/**
 * Checks if any task in the array has multiple options.
 *
 * @param tasks - The array of quote tasks to check.
 * @returns True if any task has more than one option, otherwise false.
 */
export const hasAnyTaskWithMultipleOptions = (tasks: QuoteTask[]) =>
  tasks?.some((task) => Array.isArray(task.options) && task.options.length > 1);

export const calculateMaterialTotal = (
  material: CreateQuoteFormValues['tasks'][0]['options'][0]['materials'][0],
) => {
  const materialCostNum = Number(material?.cost_in_cents) / 100 || 0;
  const quantityNum = Number(material?.quantity) || 0;
  const markUpNum = Number(material?.markup_fee_type_value) || 0;
  const markUpType = material?.markup_fee_type;

  const totalMaterialCost =
    material?.cost_type === 'total' ? materialCostNum : materialCostNum * quantityNum;

  const totalMarkUpFee = markUpType === 'fixed' ? markUpNum : (totalMaterialCost * markUpNum) / 100;

  return Number(totalMaterialCost) + Number(totalMarkUpFee);
};

export const formatResponseForQuoteSource: (response: QuoteResponse) => QuoteDetailsObj = (
  response: QuoteResponse,
) => ({
  ...response,
  tasks: response?.tasks?.map((task) => ({
    ...task,
    issue: task?.work_order_issue?.issue_id
      ? {
          issue_id: task?.work_order_issue?.issue_id,
          issue_title: task?.title,
        }
      : undefined,
    options: task?.options?.map((option) => ({
      ...option,
      materials: option?.materials?.map((material) => ({
        ...material,
        total_cost_in_cents_including_markup: calculateMaterialTotal(material) * 100 || 0,
      })),
    })),
  })),
});

export const quantityTypeAbbr: { [key: string]: string } = {
  pounds: 'lbs',
  'linear-ft': 'lf',
  'square-ft': 'sq ft',
  units: 'units',
};

export const renderMaterialCostsBreakDown = (material: QuoteOption['materials'][0]) => {
  const { cost_type: costType, quantity_type: quantityType, label, quantity } = material;
  let materialCostLabel;
  if (costType === 'per-unit') {
    materialCostLabel = `${label} (${quantity} ${quantityTypeAbbr[quantityType]} x ${formatNumber((material?.cost_in_cents || 0) / 100, 2)})`;
  } else {
    materialCostLabel = `${label} (${quantity} ${quantityTypeAbbr[quantityType]} total)`;
  }
  if (material?.markup_fee_type) {
    if (material?.markup_fee_type === 'percentage' && material?.markup_fee_type_value) {
      materialCostLabel += ` + Markup (${material?.markup_fee_type_value}%)`;
    }
    if (material?.markup_fee_type === 'fixed' && material?.markup_fee_type_value) {
      materialCostLabel += ` + Markup (${formatNumber(material?.markup_fee_type_value, 2)})`;
    }
  }
  return materialCostLabel;
};

export const returnQuoteTotal = (quoteEstimation: QuoteDetailsObj['quote_estimation_range']) => {
  if (
    !quoteEstimation ||
    quoteEstimation.min_in_cents == null ||
    quoteEstimation.max_in_cents == null
  ) {
    return '$ -';
  }

  const { min_in_cents: min, max_in_cents: max } = quoteEstimation;

  if (min === max) {
    return formatNumber(min / 100, 2);
  }

  return `${formatNumber(min / 100, 2)} - ${formatNumber(max / 100, 2)}`;
};
